import { Box, CircularProgress, styled, Typography, useTheme } from '@mui/material'
import { Add } from '@mui/icons-material'
import { useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from 'src/components/common/Chip'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { permissionValue } from 'src/enums/permissionValue'
import { useDialogCtx, useUserCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { extractPlainText, formatRelativeTime, getThemeColor } from 'src/utils'
import NoNews from '../../assets/svg/no-locality-content.svg'
import { CenteredSpinner } from '../Spinner'
import { useInfiniteNewsFeed } from '../../api/news'
import { INewsArticleListItem } from 'src/generated-types/news'
import CreateNewsArticleDialog from 'src/screens/customer/news/CreateNewsArticleDialog'
import NewsDetailDialog from 'src/screens/customer/news/NewsDetailDialog'
import { NewsType } from 'src/enums'

export const getNewsTypeLabels = (
  t: (key: string, options?: Record<string, unknown>) => string,
): Record<NewsType, string> => ({
  [NewsType.General]: t('General', { ns: 'news' }),
  [NewsType.SystemUpdate]: t('SystemUpdate', { ns: 'news' }),
  [NewsType.Maintenance]: t('Maintenance', { ns: 'news' }),
  [NewsType.Safety]: t('Safety', { ns: 'news' }),
  [NewsType.Regulation]: t('Regulation', { ns: 'news' }),
})

interface NewsFeedProps {
  customerId: EntityId
  isCustomerAdmin?: boolean
  hasLocalities?: boolean
  hasServiceStations?: boolean
  hasStorages?: boolean
}

const NewsFeed = ({ customerId, isCustomerAdmin, hasLocalities, hasServiceStations, hasStorages }: NewsFeedProps) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['common', 'news'])
  const newsTypeLabels = getNewsTypeLabels(translate)
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteNewsFeed(customerId)
  const { showDialog } = useDialogCtx()
  const { user } = useUserCtx()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const isFetchingRef = useRef(false)

  const isSystemAdmin =
    user?.permission != null &&
    (BigInt(user.permission) & BigInt(permissionValue.SystemAdminPermission)) ===
      BigInt(permissionValue.SystemAdminPermission)

  const canCreateNews = isSystemAdmin || isCustomerAdmin

  const handleCreate = useCallback(async () => {
    await showDialog(
      CreateNewsArticleDialog,
      {
        componentProps: {
          customerId: customerId as number,
          hasLocalities,
          hasServiceStations,
          hasStorages,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }, [customerId, showDialog, hasLocalities, hasServiceStations, hasStorages])

  const handleArticleClick = useCallback(
    async (articleId: number) => {
      await showDialog(
        NewsDetailDialog,
        {
          title: translate('news-feed', { ns: 'news' }),
          componentProps: { customerId, articleId },
          opts: { maxWidth: 'lg', fullWidth: true },
        },
        undefined,
        false,
      )
    },
    [customerId, showDialog],
  )

  useEffect(() => {
    isFetchingRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      if (isFetchingRef.current || !hasNextPage) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      if (scrollPercentage >= 0.85) {
        isFetchingRef.current = true
        fetchNextPage().finally(() => {
          setTimeout(() => {
            isFetchingRef.current = false
          }, 300)
        })
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [hasNextPage, fetchNextPage])

  if (isLoading) return <CenteredSpinner />

  const articles = data ?? []

  if (articles.length === 0) {
    return (
      <>
        {canCreateNews && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Chip
              icon={<Add sx={{ fontSize: 14 }} />}
              label={translate('create', { ns: 'common' })}
              size="small"
              onClick={handleCreate}
              sx={{ cursor: 'pointer' }}
              color="primary"
            />
          </Box>
        )}
        <Box py={8} className="text-center" height={'80%'}>
          <EmptyStatusCard
            imageWidth="80px"
            image={NoNews}
            header={translate('news-empty-status-title', { ns: 'common' })}
            content={translate('news-empty-status-content', { ns: 'common' })}
            headerFontSize="18px"
            contentFontSize="14px"
            contentTopMargin="10px"
            imageContainerClasses="pb-1"
            height="100%"
            contentClasses="mt-1"
          />
        </Box>
      </>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {canCreateNews && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, flexShrink: 0 }}>
          <Chip
            icon={<Add sx={{ fontSize: 14 }} />}
            label={translate('create', { ns: 'common' })}
            size="small"
            onClick={handleCreate}
            sx={{ cursor: 'pointer' }}
            color="primary"
          />
        </Box>
      )}
      <Box ref={scrollContainerRef} sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {articles.map(article => (
          <NewsContainer key={article.id} onClick={() => handleArticleClick(article.id)} sx={{ cursor: 'pointer' }}>
            <Box sx={{ flex: 1, minWidth: 0, py: 0.5, px: 1 }}>
              <Flex.Row sx={{ alignItems: 'center', mb: 0.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {article.title || 'Untitled'}
                </Typography>
                <StyledChip
                  variant="filled"
                  label={newsTypeLabels[article.newsType]}
                  bgColor={MUIThemeColors.primaryMain}
                />
                {article.isImportant && (
                  <StyledChip
                    variant="filled"
                    label={translate('is-important', { ns: 'news' })}
                    bgColor={MUIThemeColors.errorMain}
                  />
                )}
              </Flex.Row>
              <Typography
                variant="caption"
                color={getThemeColor(theme, MUIThemeColors.primaryMain)}
                sx={{ display: 'block', mb: 0.25 }}
              >
                {formatAuthorName(article)} | {formatRelativeTime(article.createdAt.toString())}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {extractPlainText(article.content)}
              </Typography>
            </Box>
            {article.hasImage && article.thumbnailUrl && (
              <Box sx={{ width: 64, height: 64, flexShrink: 0, ml: 1, alignSelf: 'center' }}>
                <img
                  src={article.thumbnailUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                />
              </Box>
            )}
          </NewsContainer>
        ))}
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

function formatAuthorName(article: INewsArticleListItem): string {
  const parts = [article.createdByUserFirstName, article.createdByUserLastName].filter(Boolean)
  return parts.join(' ') || 'System'
}

export default NewsFeed

// Styled components
const NewsContainer = styled(Box)`
  padding: 9px 8px 9px 10px;
  border: 1px solid ${props => getThemeColor(props.theme, MUIThemeColors.secondaryLight)};
  display: flex;
  flex-direction: row;
  border-radius: 5px;
  margin-top: 4px;

  &:hover {
    background-color: ${props => getThemeColor(props.theme, MUIThemeColors.secondaryLight)};
  }
`

export const StyledChip = styled(Chip)<{ bgColor: MUIThemeColors }>`
  height: 14px;
  font-size: 9px;
  margin-top: 3px;
  margin-left: 8px;
  background-color: ${props => getThemeColor(props.theme, props.bgColor)};
  color: ${props => getThemeColor(props.theme, MUIThemeColors.white)};
`
